
export const nav = {
  services: "Our Services",
  process: "Process",
  universities: "Universities",
  testimonials: "Testimonials",
  faq: "FAQ",
  contact: "Contact Us"
};

export const hero = {
  title: "Make Your Dream of Studying in the USA Come True",
  subtitle: "We support students from French-speaking Africa through every step of their academic journey in the United States. From admission to settlement, we're by your side to ensure your success.",
  ctaStart: "Get Started Now",
  ctaDiscover: "Discover Our Services",
  stats: {
    students: "Students Supported",
    success: "Success Rate",
    universities: "Partner Universities"
  }
};

export const services = {
  title: "Our Complete Services",
  subtitle: "Personalized support at every step of your journey to ensure your academic success in the United States",
  items: [
    {
      title: "Admissions Assistance",
      description: "We help you prepare a complete and competitive admission file for American universities. Writing motivation letters, academic CV, and personalized advice to maximize your chances of acceptance."
    },
    {
      title: "Student Visa Assistance",
      description: "Complete support for obtaining your F-1 visa. Preparation for consular interviews, verification of required documents, and coaching to succeed in your interview at the US embassy."
    },
    {
      title: "Scholarships and Financing",
      description: "Research and application for scholarships available to international students. We guide you to the best financing and financial aid opportunities suited to your academic profile."
    },
    {
      title: "Housing and Settlement",
      description: "Help finding housing on or off campus. We assist you with administrative procedures and provide all the necessary information for a successful settlement in the United States."
    },
    {
      title: "Language Tests",
      description: "Preparation for TOEFL, IELTS and other required English language exams. Preparation courses, practice tests, and strategies to obtain the scores necessary for your university admission."
    },
    {
      title: "Post-Arrival Support",
      description: "Ongoing support after your arrival in the United States. Assistance with opening a bank account, obtaining a social security number, campus orientation, and integration into American student life."
    }
  ]
};

export const process = {
  title: "Our 6-Step Process",
  subtitle: "A structured and proven approach to transform your dream of studying in the United States into reality",
  steps: [
    {
      title: "Initial Consultation",
      description: "Assessment of your academic profile, study goals and university preferences. Together we establish a personalized plan for your journey to the United States."
    },
    {
      title: "University Selection",
      description: "Identification of American universities matching your profile, aspirations and budget. We help you choose the best options to maximize your chances of success."
    },
    {
      title: "Application Preparation",
      description: "Complete preparation of your admission file with all required documents. Writing and reviewing your essays, motivation letters and academic recommendations."
    },
    {
      title: "Application Submission",
      description: "Sending your applications to selected universities within deadlines. Rigorous tracking of each application's progress and communication with admissions offices."
    },
    {
      title: "Visa Approval",
      description: "Complete preparation for your F-1 visa application. Coaching for the consular interview, document verification and support until you obtain your student visa."
    },
    {
      title: "Pre-Departure Preparation",
      description: "Organization of your trip, housing search, pre-departure orientation and all practical information for your successful settlement in the United States."
    }
  ]
};

export const universities = {
  title: "Our Partner Universities",
  subtitle: "Access the best American academic institutions through our established partnership network",
  learnMore: "Learn More"
};

export const testimonials = {
  title: "Student Testimonials",
  subtitle: "Discover the success stories of our students who achieved their American dream with our support"
};

export const faq = {
  title: "Frequently Asked Questions",
  subtitle: "Find answers to the most common questions about studying in the United States",
  items: [
    {
      question: "What are the admission criteria for American universities?",
      answer: "Criteria vary by university, but generally include: a strong academic record (grades and diplomas), standardized test scores (TOEFL/IELTS for English, SAT/ACT for undergraduate, GRE/GMAT for graduate programs), letters of recommendation, a compelling personal essay, and sometimes professional experience or extracurricular activities. We help you prepare a complete file that meets all these criteria."
    },
    {
      question: "How long does the admission process take?",
      answer: "The complete process typically takes between 6 and 12 months. It's recommended to start at least one year before your desired enrollment date. This includes language test preparation, file preparation, application submission, waiting for responses, and visa procedures. We guide you throughout this process to meet all important deadlines."
    },
    {
      question: "What financing options are available?",
      answer: "Several options exist: merit scholarships offered by universities, government scholarships (Fulbright, etc.), need-based financial aid, teaching or research assistantships for graduate programs, international student loans, and on-campus work programs. We help you identify and apply for the best financing opportunities suited to your profile."
    },
    {
      question: "How do I obtain an F-1 student visa?",
      answer: "After being accepted by an American university, you will receive an I-20 form. You must then: pay SEVIS fees, complete the DS-160 form, schedule an embassy appointment, prepare required documents (passport, I-20, proof of funding, etc.), and attend the consular interview. We thoroughly prepare you for each step and coach you for the interview."
    },
    {
      question: "What is the cost of studying in the United States?",
      answer: "Tuition fees vary considerably: from $20,000 to $60,000 per year depending on the university and program. You must also budget for living expenses (housing, food, transportation) estimated between $10,000 and $20,000 per year. Public universities are generally less expensive than private ones. We help you find affordable options and funding sources."
    },
    {
      question: "Can I work while studying?",
      answer: "Yes, with an F-1 visa, you can work on campus up to 20 hours per week during classes and full-time during vacations. After the first year, you can request CPT (Curricular Practical Training) authorization for internships related to your studies. After graduation, OPT (Optional Practical Training) allows you to work up to 12 months (36 months for STEM degrees)."
    },
    {
      question: "What documents are needed for the application?",
      answer: "Required documents include: official transcripts translated into English, obtained diplomas, language test scores (TOEFL/IELTS), SAT/ACT or GRE/GMAT scores depending on level, letters of recommendation (2-3), personal essay or motivation letter, academic CV, proof of funding, passport copy, and sometimes a portfolio for certain programs. We guide you in preparing each document."
    },
    {
      question: "Do you offer support after arrival in the United States?",
      answer: "Absolutely! Our support doesn't stop at your departure. We continue to support you after your arrival: settlement assistance, campus orientation, advice for opening a bank account, obtaining a social security number, adaptation to American student life, and assistance with any administrative questions. We remain available throughout your studies."
    }
  ]
};

export const cta = {
  title: "Ready to Start Your American Adventure?",
  subtitle: "Contact us today for a free consultation and discover how we can help you achieve your dream of studying in the United States",
  form: {
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phone: "Phone",
    country: "Country of Origin",
    studyLevel: "Desired Study Level",
    message: "Message",
    selectLevel: "Select a level",
    bachelor: "Bachelor's Degree",
    master: "Master's Degree",
    phd: "Doctorate (PhD)",
    other: "Other",
    submit: "Submit My Request",
    required: "*",
    maxChars: "Maximum 500 characters",
    placeholders: {
      firstName: "Your first name",
      lastName: "Your last name",
      email: "your.email@example.com",
      phone: "+221 XX XXX XX XX",
      country: "E.g.: Senegal, Ivory Coast...",
      message: "Tell us about your study project in the United States... (500 characters maximum)"
    },
    alerts: {
      maxLength: "The message cannot exceed 500 characters.",
      success: "Your request has been sent successfully! We will contact you soon.",
      error: "An error occurred. Please try again."
    }
  }
};

export const footer = {
  description: "Your trusted partner to achieve your dream of studying in the United States. Personalized support from admission to settlement.",
  servicesTitle: "Services",
  quickLinksTitle: "Quick Links",
  contactTitle: "Contact",
  services: {
    admissions: "Admissions Assistance",
    visa: "Visa Assistance",
    scholarships: "Scholarships and Financing",
    housing: "Housing and Settlement",
    language: "Language Tests"
  },
  links: {
    process: "Our Process",
    universities: "Partner Universities",
    testimonials: "Testimonials",
    faq: "FAQ",
    contact: "Contact"
  },
  privacy: "Privacy Policy",
  terms: "Terms of Use",
  poweredBy: "Powered by Readdy",
  rights: "All rights reserved."
};

export default {
  nav,
  hero,
  services,
  process,
  universities,
  testimonials,
  faq,
  cta,
  footer
};
